// packages/core/src/quizzes/content/moneyIntuition.ts
// ─────────────────────────────────────────────────────────────────────────
// "How Good Is Your Money Intuition?" — the launch discovery quiz.
//
// Eight estimation questions. Every answer below was computed, not vibed:
//
//   Q1  FV of $500/mo, 30y @ 7% (monthly compounding)      = $609,985
//   Q2  growth share of Q1                                  = 70.5%
//   Q3  doubling time @ 7% annual                           = 10.24y
//   Q4  $10k @25 vs $20k @45, both 7% to 65 — A/B multiple  = 1.93×
//   Q5  buying power of $100 after 30y @ 3% inflation       = $41.20
//   Q6  final-balance share consumed by a 1% fee over 40y   = 31.3%
//   Q7  years to FI at a 50% savings rate (5% real, 4% SWR) = 16.6y
//   Q8  $500/mo @ 7%: start-at-25 minus start-at-35, at 65  = $702,421
//
// Assumptions are stated inside each prompt — the quiz never asks people
// to guess our assumptions, only the consequences of them. Show, never
// advise: reveals state what the math does, not what the user should do.
// ─────────────────────────────────────────────────────────────────────────

import type { EstimateQuizContent } from '../DiscoveryTypes';

export const MONEY_INTUITION: EstimateQuizContent = {
	meta: {
		id: 'money-intuition',
		title: 'How Good Is Your Money Intuition?',
		hook: 'Test your gut against how money really moves over time.',
		engine: 'estimate',
		minutes: 4,
		status: 'live',
	},

	questions: [
		{
			type: 'estimate',
			id: 'mi-fv-500',
			prompt: 'You invest $500 each month for 30 years. It earns a 7% average annual return. After 30 years, what is your investment sitting at?',
			unit: 'dollars',
			answer: 610000,
			goodWithin: 1.25,
			zeroBeyond: 3.5,
			bias: 'exponential_underestimate',
			biasDirection: 'under',
			reveal: 'About $610,000. You put in $180,000 of your own money. Growth created the rest. That is the power of time and money working together.',
			placeholder: 'e.g. 250,000',
		},
		{
			type: 'estimate',
			id: 'mi-growth-share',
			setup: 'Same investment: $500 each month for 30 years at 7%.',
			prompt: 'What percentage of the final balance came from growth, rather than from the money you put in?',
			unit: 'percent',
			answer: 70,
			goodWithin: 1.15,
			zeroBeyond: 2.2,
			bias: 'exponential_underestimate',
			biasDirection: 'under',
			reveal: 'About 70% came from growth. You put in $180,000 yourself. The other roughly $430,000 came from time and growth doing the work.',
			placeholder: 'e.g. 40',
		},
		{
			type: 'estimate',
			id: 'mi-doubling',
			prompt: 'Your money grows at 7% a year. How many years does it take to double?',
			unit: 'years',
			answer: 10.2,
			goodWithin: 1.2,
			zeroBeyond: 3,
			bias: 'exponential_underestimate',
			biasDirection: 'over',
			reveal: 'Just over 10 years. Growth stacks on growth, so doubling takes less time than most people expect.',
			placeholder: 'e.g. 15',
		},
		{
			type: 'estimate',
			id: 'mi-head-start',
			setup: 'Person A invests $10,000 at age 25 and never adds another dollar. Person B invests $20,000 at age 45. Both earn 7% until age 65.',
			prompt: 'Person A ends up with how many times Person B\u2019s balance?',
			unit: 'multiple',
			answer: 1.9,
			goodWithin: 1.25,
			zeroBeyond: 3,
			bias: 'time_undervalue',
			biasDirection: 'under',
			reveal: 'About 2 times as much, with half the money invested. Person A had 20 extra years for that money to grow. Time did more than the extra deposit.',
			placeholder: 'e.g. 1.5',
		},
		{
			type: 'estimate',
			id: 'mi-inflation',
			prompt: 'Inflation runs 3% a year. In 30 years, $100 in cash buys what amount of today\u2019s stuff?',
			unit: 'dollars',
			answer: 41,
			goodWithin: 1.2,
			zeroBeyond: 2.5,
			bias: 'erosion_blindspot',
			biasDirection: 'over',
			reveal: 'About $41. Inflation is compounding in reverse. The same curve that grows your investments quietly shrinks your cash.',
			placeholder: 'e.g. 70',
		},
		{
			type: 'estimate',
			id: 'mi-fee-drag',
			setup: 'Two identical investments grow for 40 years. One earns 7%. The other earns 6% after a 1% annual fee.',
			prompt: 'About what percent smaller is the account with the 1% annual fee?',
			unit: 'percent',
			answer: 31,
			goodWithin: 1.25,
			zeroBeyond: 3,
			bias: 'rate_leverage_blindspot',
			biasDirection: 'under',
			reveal: 'About 31% smaller. A 1% fee does not just cost 1% once. It lowers the growth rate every year, so the gap compounds over decades.',
			placeholder: 'e.g. 10',
		},
		{
			type: 'estimate',
			id: 'mi-credit-card-drag',
			setup: 'You owe $5,000 on a credit card at 24% APR. You pay $150 each month and add no new charges.',
			prompt: 'Roughly how many years until the balance is gone?',
			unit: 'years',
			answer: 4.6,
			goodWithin: 1.25,
			zeroBeyond: 3,
			bias: 'erosion_blindspot',
			biasDirection: 'under',
			reveal: 'About 4.5 years. The early payments are fighting interest before they make serious progress on the original balance.',
			placeholder: 'e.g. 2',
		},
		{
			type: 'estimate',
			id: 'mi-waiting-cost',
			setup: 'Person A invests $500 each month from age 25 until age 65 at 7%. Person B invests $500 each month from age 35 until age 65 at 7%.',
			prompt: 'How many dollars MORE does Person A, the earlier starter, end up with?',
			unit: 'dollars',
			answer: 700000,
			goodWithin: 1.3,
			zeroBeyond: 4,
			bias: 'time_undervalue',
			biasDirection: 'under',
			reveal: 'About $700,000 more. Person A ends up with around $1.31 million. Person B with $610,000. The first 10 years gave Person A a bigger base to grow from.',
			placeholder: 'e.g. 100,000',
		},
	],

	resultBands: [
		{
			minHits: 7,
			title: 'Calibrated',
			insight:
				'Your gut tracked the math better than most. Choices that feel small in the moment have a significantly bigger impact once time becomes a factor. Visualize this on your own situation in Nisk.',
		},
		{
			minHits: 5,
			title: 'Mostly tuned',
			insight:
				'You felt the right direction but missed the size of it, usually on the longer-horizon questions where growth outruns almost everyone\u2019s intuition. You were closer than most.',
		},
		{
			minHits: 3,
			title: 'Linear in an exponential world',
			insight:
				'You are with the majority. People tend to think in straight lines, but money grows in a curve. That gap is not a flaw. It is just something a gut cannot feel, which is exactly what the numbers are for.',
		},
		{
			minHits: 0,
			title: 'Guessing without the numbers',
			insight:
				'Almost nobody\u2019s gut does compounding, and yours just proved it honestly. This is exactly why the math matters. You do not have to feel it to use it \u2014 Nisk runs the real numbers for you.',
		},
	],

	biasCopy: {
		exponential_underestimate: {
			label: 'Linear thinking',
			insight:
				'You picture money in straight lines. It actually grows in a curve, and the longer the time frame, the more a straight-line guess falls short.',
		},
		time_undervalue: {
			label: 'Time blindness',
			insight:
				'You focus on the dollars, not the years. With compounding, WHEN you invest usually matters more than HOW MUCH. Time is the biggest lever you have.',
		},
		erosion_blindspot: {
			label: 'Erosion blindspot',
			insight:
				'You feel growth but not decay. Inflation and fees run on the same compounding math, just pointed the other way, quietly, every year.',
		},
		rate_leverage_blindspot: {
			label: 'Rate leverage',
			insight:
				'Small percentages feel small. Over decades, a single percentage point in fees, returns, or savings rate can move the outcome by a third or more.',
		},
	},

	cta: {
		label: 'Run your real numbers',
		to: '/simulate',
		blurb: 'Intuition is hard to fix, so you outsource it. Nisk runs this exact math on your own numbers and shows you the curve your gut could not guess.',
	},

	shareText:
		'I scored {hits}/{total} on Nisk\u2019s Money Intuition test. Most people underestimate how drastic compounding can be. See how calibrated your gut is: https://nisk.co/quizzes/money-intuition',
};
